import isEmpty from "lodash/isEmpty";

/**
 * Reads `upgrades` from the upgrade-package shape used across the app:
 * `{ [packageId]: { upgrades?: Record<string, UpgradeEntry> } } }`.
 *
 * When `upgradePackageId` is omitted, the first key of `upgradePackage` is used — same as existing
 * `const packageKey = Object.keys(upgradePackage)[0]` patterns in `utils/upgrades/index.jsx`.
 *
 * @param {Record<string, { upgrades?: Record<string, object> }> | null | undefined} upgradePackage
 * @param {string | undefined} [upgradePackageId] - Use this package id instead of the first key.
 * @returns {{ packageKey: string | null, upgrades: Record<string, object> }}
 */
export function resolveUpgradesFromUpgradePackage(upgradePackage, upgradePackageId) {
    if (upgradePackage == null || typeof upgradePackage !== "object" || isEmpty(upgradePackage)) {
        return { packageKey: null, upgrades: {} };
    }

    const keys = Object.keys(upgradePackage);
    const packageKey =
        upgradePackageId != null && keys.includes(upgradePackageId) ? upgradePackageId : (keys[0] ?? null);

    if (packageKey == null) {
        return { packageKey: null, upgrades: {} };
    }

    const upgrades = upgradePackage[packageKey]?.upgrades ?? {};
    return { packageKey, upgrades: upgrades && typeof upgrades === "object" ? upgrades : {} };
}

/**
 * Finds the first upgrade in a package whose `upgradeType` matches, and returns its `fields`
 * (and the resolved ids for convenience).
 *
 * @param {Record<string, { upgrades?: Record<string, object> }> | null | undefined} upgradePackage
 * @param {string} upgradeType - e.g. matching `upgrade.upgradeType` in the package.
 * @param {string | undefined} [upgradePackageId] - Package id; defaults to first key on `upgradePackage`.
 * @returns {{
 *   packageKey: string | null,
 *   fields: object | undefined,
 *   upgrade: object | null,
 *   upgradeId: string | null,
 * }}
 */
export function getUpgradeMatchForType(upgradePackage, upgradeType, upgradePackageId) {
    const { packageKey, upgrades } = resolveUpgradesFromUpgradePackage(upgradePackage, upgradePackageId);
    if (upgradeType == null || upgradeType === "") {
        return { packageKey, fields: undefined, upgrade: null, upgradeId: null };
    }

    for (const [id, upgrade] of Object.entries(upgrades)) {
        if (upgrade && typeof upgrade === "object" && (upgrade.upgradeType ?? "") === upgradeType) {
            return {
                packageKey,
                fields: upgrade.fields,
                upgrade,
                upgradeId: id,
            };
        }
    }

    return { packageKey, fields: undefined, upgrade: null, upgradeId: null };
}
